import React from 'react';
import * as Sentry from '@sentry/react';

type Props = { children: React.ReactNode };
type State = { hasError: boolean; error?: Error; eventId?: string };

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Erro capturado pelo ErrorBoundary:', error, info);
    }

    // Report to Sentry
    Sentry.withScope((scope) => {
      scope.setContext('react_error_info', {
        componentStack: info.componentStack,
      });
      const eventId = Sentry.captureException(error);
      this.setState({ eventId });
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-xl shadow border p-6 max-w-md w-full text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Ocorreu um erro na aplicação</h1>
            <p className="text-gray-600 mb-4">Tente recarregar a página. Se persistir, entre em contato com o suporte.</p>
            {import.meta.env.DEV && this.state.error?.message && (
              <pre className="text-xs text-gray-500 bg-gray-50 border rounded p-3 overflow-auto max-h-40 text-left mb-4">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex gap-2 justify-center">
              <button
                className="inline-flex items-center justify-center h-10 px-4 rounded bg-brand-600 text-white hover:bg-brand-700"
                onClick={() => window.location.reload()}
              >
                Recarregar
              </button>
              {this.state.eventId && (
                <button
                  className="inline-flex items-center justify-center h-10 px-4 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={() => Sentry.showReportDialog({ eventId: this.state.eventId })}
                >
                  Reportar Erro
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
